import React, { useCallback, useEffect, useState } from 'react';
import { FileDown, ShieldCheck, BellRing, Loader2, AlertTriangle } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { SkeletonRows } from './OrgPanelShared';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const REPORT_TYPES = [
  { id: 'workforce', label: 'Workforce Summary', desc: 'Per-worker assignments, hours, attendance quality' },
  { id: 'attendance', label: 'Attendance Ledger', desc: 'Every check-in with timing, method and flags' },
  { id: 'shifts', label: 'Shifts Report', desc: 'All shifts with status, workers and pay' },
  { id: 'timesheets', label: 'Timesheets', desc: 'Approved hours ready for payroll' },
];
const PERIODS = ['daily', 'weekly', 'monthly', 'quarterly', 'annual', 'custom'];

export function ReportsComplianceTab() {
  return (
    <div className="space-y-5" data-testid="reports-compliance-tab">
      <ComplianceMonitor />
      <ReportBuilder />
    </div>
  );
}

function ComplianceMonitor() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiCall('/api/org/compliance/expiring?days=60');
      setData(r);
    } catch (e) { toast.error(e.message); setData({}); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendReminders = async () => {
    setBusy(true);
    try {
      const r = await apiCall('/api/org/compliance/send-reminders', { method: 'POST', body: JSON.stringify({ days: 30 }) });
      toast.success(`${r.sent} reminder(s) sent · ${r.skipped_recent} skipped (recently notified)`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (data === null) return <SkeletonRows n={3} />;
  const { expired = [], expiring = [], background_check_failed: bg = [] } = data;
  const issues = expired.length + expiring.length + bg.length;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="compliance-monitor">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-sky-600" /> Compliance Monitor (60 days)
        </p>
        <button onClick={sendReminders} disabled={busy || !expiring.length}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-600 text-white text-xs font-semibold hover:bg-sky-700 disabled:opacity-50"
          data-testid="compliance-send-reminders-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />} Send renewal reminders
        </button>
      </div>
      {!issues ? (
        <p className="text-sm text-emerald-600" data-testid="compliance-all-clear">All roster credentials are current — nothing expiring within 60 days.</p>
      ) : (
        <div className="space-y-2">
          {expired.map((r, i) => (
            <Row key={`e${i}`} tone="red" testid={`compliance-expired-${r.credential_id}`}
              text={<><b>{r.worker}</b> — {r.credential} EXPIRED {r.expiry_date}{r.override && ' (override active)'}</>}
              sub="Blocked from new assignments until renewed or overridden" />
          ))}
          {bg.map((r, i) => (
            <Row key={`b${i}`} tone="red" testid={`compliance-bg-${r.worker_id}`}
              text={<><b>{r.worker}</b> — background check failed{r.override && ' (override active)'}</>}
              sub="Blocked from new assignments" />
          ))}
          {expiring.map((r, i) => (
            <Row key={`x${i}`} tone="amber" testid={`compliance-expiring-${r.credential_id}`}
              text={<><b>{r.worker}</b> — {r.credential} expires {r.expiry_date}</>}
              sub="Send a reminder so they renew in time" />
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-400 mt-2">{data.blocking_note}</p>
    </div>
  );
}

function Row({ tone, text, sub, testid }) {
  return (
    <div className={`flex items-start gap-2 rounded-lg px-3 py-2 ${tone === 'red' ? 'bg-red-50' : 'bg-amber-50'}`} data-testid={testid}>
      <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${tone === 'red' ? 'text-red-500' : 'text-amber-500'}`} />
      <div className="min-w-0">
        <p className="text-xs text-slate-800">{text}</p>
        <p className="text-[11px] text-slate-500">{sub}</p>
      </div>
    </div>
  );
}

function ReportBuilder() {
  const [type, setType] = useState('workforce');
  const [period, setPeriod] = useState('monthly');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState('');

  const download = async (format) => {
    setBusy(format);
    try {
      const params = new URLSearchParams({ format });
      if (period !== 'custom') params.set('period', period);
      else { if (from) params.set('from', from); if (to) params.set('to', to); }
      const res = await fetch(`${BACKEND}/api/reports/${type}?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error((await res.json()).detail || 'Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_${period}.${format === 'pdf' ? 'pdf' : 'csv'}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${type} report downloaded (${format.toUpperCase()})`);
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="report-builder">
      <p className="text-xs font-bold text-slate-500 uppercase mb-3 flex items-center gap-1.5">
        <FileDown className="w-4 h-4 text-indigo-600" /> Report Builder
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        {REPORT_TYPES.map((r) => (
          <label key={r.id} className={`border rounded-xl p-3 cursor-pointer transition ${type === r.id ? 'border-indigo-500 bg-indigo-50/50' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`report-type-${r.id}`}>
            <input type="radio" name="rpt" className="hidden" checked={type === r.id} onChange={() => setType(r.id)} />
            <p className="text-sm font-semibold text-slate-900">{r.label}</p>
            <p className="text-xs text-slate-500">{r.desc}</p>
          </label>
        ))}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        {PERIODS.map((p) => (
          <button key={p} onClick={() => setPeriod(p)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium capitalize ${period === p ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}
            data-testid={`report-period-${p}`}>{p}</button>
        ))}
        {period === 'custom' && (
          <span className="flex items-center gap-1.5 ml-2">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1 text-xs" data-testid="report-from" />
            <span className="text-xs text-slate-400">to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1 text-xs" data-testid="report-to" />
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <button onClick={() => download('csv')} disabled={!!busy}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50"
          data-testid="report-download-csv">
          {busy === 'csv' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Download CSV
        </button>
        <button onClick={() => download('pdf')} disabled={!!busy}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:bg-slate-900 disabled:opacity-50"
          data-testid="report-download-pdf">
          {busy === 'pdf' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Download PDF
        </button>
      </div>
    </div>
  );
}

export default ReportsComplianceTab;
